import type { ReactNode } from "react";
import { Platform, Pressable, StyleSheet, View } from "react-native";

import { webTouchControl } from "@/design/interactionMotion";
import { Overline, Text } from "@/design/Text";
import {
  hoverRingStrong,
  palette,
  playerGlassBlur,
  radius,
  space
} from "@/design/tokens";

const glassBlur = Platform.OS === "web" && (playerGlassBlur as object);

export function RemotePlaybackRouteChip({
  icon,
  label,
  accessibilityLabel,
  active = false,
  disabled = false,
  onPress,
  onInteractionStart,
  onInteractionEnd
}: {
  icon: ReactNode;
  label: string;
  accessibilityLabel?: string;
  active?: boolean;
  disabled?: boolean;
  onPress?: () => void;
  onInteractionStart?: () => void;
  onInteractionEnd?: () => void;
}) {
  const content = (
    <>
      <View style={[styles.icon, { pointerEvents: "none" }]}>
        {icon}
      </View>
      <Text
        variant="smallStrong"
        color={active ? palette.accent : palette.text}
        numberOfLines={1}
      >
        {label}
      </Text>
    </>
  );

  if (!onPress) {
    return (
      <View style={[styles.chip, glassBlur, active && styles.chipActive]}>
        {content}
      </View>
    );
  }

  return (
    <Pressable
      accessibilityLabel={accessibilityLabel ?? label}
      accessibilityRole="button"
      disabled={disabled}
      hitSlop={6}
      onBlur={onInteractionEnd}
      onFocus={onInteractionStart}
      onPress={onPress}
      style={({ hovered, pressed }) => [
        styles.chip,
        webTouchControl,
        glassBlur,
        active && styles.chipActive,
        hovered && (active ? styles.chipActiveHovered : styles.hovered),
        pressed && styles.pressed
      ]}
    >
      {content}
    </Pressable>
  );
}

export function RemotePlaybackEyebrow({ children }: { children: ReactNode }) {
  return (
    <Overline color={palette.textSoft} style={styles.eyebrow}>
      {children}
    </Overline>
  );
}

/** Shared type rhythm for every centered player-state hero. */
export function RemotePlaybackTitleBlock({
  eyebrow,
  numberOfLines = 1,
  title
}: {
  eyebrow: ReactNode;
  numberOfLines?: number;
  title: string;
}) {
  return (
    <View style={styles.titleBlock}>
      <RemotePlaybackEyebrow>{eyebrow}</RemotePlaybackEyebrow>
      <Text
        color={palette.text}
        numberOfLines={numberOfLines}
        style={styles.title}
        variant="display"
      >
        {title}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  chip: {
    alignItems: "center",
    backgroundColor: palette.playerGlass,
    borderColor: palette.hairlineStrong,
    borderRadius: radius.pill,
    borderWidth: 1,
    flexDirection: "row",
    gap: 8,
    minHeight: 40,
    paddingHorizontal: space.lg
  },
  // Active = the app's tinted info-button language (see design/Button.tsx):
  // soft accent wash and border, accent ink — a lit control, never a neon ring.
  chipActive: {
    backgroundColor: "rgba(143, 168, 250, 0.12)",
    borderColor: "rgba(143, 168, 250, 0.3)"
  },
  chipActiveHovered: {
    backgroundColor: "rgba(143, 168, 250, 0.2)",
    borderColor: "rgba(143, 168, 250, 0.5)"
  },
  // Hover is a lift, never a plunge: the glass warms by a cool wash and the
  // hairline brightens one step — same rhythm as the audio chip's segments.
  hovered: {
    backgroundColor: palette.playerGlassHover,
    ...hoverRingStrong
  },
  pressed: {
    opacity: 0.72
  },
  icon: {
    alignItems: "center",
    height: 16,
    justifyContent: "center",
    width: 16
  },
  eyebrow: {
    transform: [{ translateY: 3 }]
  },
  titleBlock: {
    alignItems: "center",
    gap: space.xs
  },
  title: {
    textAlign: "center"
  }
});
